import { useFragment } from '@apollo/client/react';
import {
  BTC_GAME_FRAGMENT,
  BTC_PIPELINE_FRAGMENT,
} from '@optools/services/operator-gateway/btc/fragments';
import { CopyrightStream } from '@optools/tools/btc-tools/components/copyright-streams/copyright-stream';
import { BroadcastSourceControls } from '@optools/tools/btc-tools/components/pipeline-detail/broadcast-source-controls';
import { EVIControls } from '@optools/tools/btc-tools/components/pipeline-detail/evi/evi-controls';
import { PipelineDetailHeader } from '@optools/tools/btc-tools/components/pipeline-detail/pipeline-detail-header';
import { PipelineMedia } from '@optools/tools/btc-tools/components/pipeline-media';
import {
  pipelineParamsSchema,
  pipelineSearchSchema,
} from '@optools/tools/btc-tools/schemas/pipeline';
import { getMediaId, getTeamByFeedType, hasVideoMedia } from '@optools/utils/pipeline';
import { createFileRoute, stripSearchParams, useParams, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_auth/btc-tools/schedule/$gamePk/pipelines/$pipelineId')({
  component: RouteComponent,
  params: { parse: zodValidator(pipelineParamsSchema).parse },
  validateSearch: zodValidator(pipelineSearchSchema),
  search: { middlewares: [stripSearchParams({ asset: '', mediaId: '' })] },
});
/* eslint-enable perfectionist/sort-objects */

export function RouteComponent() {
  const { date } = useSearch({ from: '/_auth/btc-tools/schedule' });
  const { gamePk, pipelineId } = useParams({
    from: '/_auth/btc-tools/schedule/$gamePk/pipelines/$pipelineId',
  });

  const gameFragment = useFragment({
    fragment: BTC_GAME_FRAGMENT,
    fragmentName: 'BTCGameFields',
    from: { __typename: 'Game', gamePk },
  });

  const pipelineFragment = useFragment({
    fragment: BTC_PIPELINE_FRAGMENT,
    from: { __typename: 'MediaPipeline', id: pipelineId },
  });

  if (!pipelineFragment.complete) {
    return (
      <div className="flex justify-center">
        <h1 className="text-lg font-bold">Could not find pipeline with ID: {pipelineId}</h1>
      </div>
    );
  }

  if (!gameFragment.complete) return null;

  const { data: game } = gameFragment;
  const { data: pipeline } = pipelineFragment;

  const { away, gameDate, home } = game;
  const { eviPod, eviStatus, feedType, status } = pipeline;

  const mediaId = getMediaId(pipeline);
  const isVideoPipeline = hasVideoMedia(pipeline);
  const team = getTeamByFeedType({ away, feedType, home });

  return (
    <section className="flex flex-col gap-16">
      <div className="flex flex-col gap-2">
        <PipelineDetailHeader
          date={date}
          gamePk={gamePk}
          mediaId={mediaId}
          pipeline={pipeline}
          team={team}
        />

        <div className="flex flex-wrap gap-2">
          {isVideoPipeline ? (
            <BroadcastSourceControls
              date={date}
              feedType={feedType}
              gamePk={gamePk}
              mediaId={mediaId}
              pipeline={pipeline}
            />
          ) : null}
          <EVIControls
            eviPod={eviPod}
            eviStatus={eviStatus}
            mediaId={mediaId}
            pipelineStatus={status}
          />
        </div>
      </div>

      <CopyrightStream
        awayTriCode={away.abbreviation}
        feedType={feedType}
        gamePk={gamePk}
        gameStartTime={gameDate}
        homeTriCode={home.abbreviation}
        scheduleDate={date}
      />

      <PipelineMedia gamePk={gamePk} pipeline={pipeline} />
    </section>
  );
}
